<template>
    <v-dialog
        :model-value="modelValue"
        max-width="600px"
        persistent
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card>
            <v-card-title class="text-h5">
                {{ $t('messages.create_new_ticket') }}
            </v-card-title>

            <v-card-text>
                <v-form ref="form" @submit.prevent="submitForm">
                    <v-text-field
                        v-model="subject"
                        :label="$t('messages.subject')"
                        required
                        :rules="[v => !!v || $t('messages.subject_required')]"
                        variant="outlined"
                        class="mb-4"
                    />

                    <v-textarea
                        v-model="message"
                        :label="$t('messages.message')"
                        required
                        :rules="[v => !!v || $t('messages.message_required')]"
                        variant="outlined"
                        rows="5"
                    />
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn color="grey" variant="text" @click="closeDialog">
                    {{ $t('messages.cancel') }}
                </v-btn>
                <v-btn color="primary" :loading="isSubmitting" @click="submitForm">
                    {{ $t('messages.create_ticket') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Vue, Prop, Watch, Setup} from 'vue-facing-decorator'
    import {authStore} from '@/composables/store-accessor'
    import {useNuxtApp} from '#app'

    @Component
    export default class NewTicketModal extends Vue {
        @Prop({default: false}) modelValue!: boolean
        @Prop({required: true}) companyId!: string

        subject: string = ''
        message: string = ''
        isSubmitting: boolean = false

        mounted() {
        }

        // Use authStore.isTO1 to check if the user has TO1 role
        get isTO1User(): boolean {
            return authStore.isTO1
        }

        @Watch('modelValue')
        onModelValueChange(newVal: boolean, oldVal: boolean) {
        }

        closeDialog() {
            this.$emit('update:modelValue', false)
        }

        async submitForm() {
            const form = this.$refs.form as any

            if (!form.validate()) {
                return
            }

            this.isSubmitting = true

            try {
                // Log debug information before submitting

                const token = useCookie('token').value

                if (!token) {
                    throw new Error('Authentication token not found. Please log in.')
                }

                // Get the selected company's internal ID
                // We need to use the selected company's internal ID, not the Zero Star Hotels company ID
                let companyInternalId = localStorage.getItem(`company_${this.companyId}_internalId`)

                // MongoDB fallback for companyInternalId
                if (!companyInternalId) {
                    try {
                        const fallbackResp = await fetch(`/proxy/messaging/config?key=company_${this.companyId}_internalId`, {
                            headers: {Accept: 'application/json', Token: token},
                        })
                        if (fallbackResp.ok) {
                            const fallbackData = await fallbackResp.json()
                            if (fallbackData.value) {
                                companyInternalId = fallbackData.value
                                localStorage.setItem(`company_${this.companyId}_internalId`, companyInternalId!)
                            }
                        }
                    } catch { /* ignore */ }
                }


                // Get currentUserContactId with MongoDB fallback
                let currentUserContactId = localStorage.getItem('currentUserContactId')
                if (!currentUserContactId) {
                    try {
                        const fallbackResp = await fetch('/proxy/messaging/config?key=currentUserContactId', {
                            headers: {Accept: 'application/json', Token: token},
                        })
                        if (fallbackResp.ok) {
                            const fallbackData = await fallbackResp.json()
                            if (fallbackData.value) {
                                currentUserContactId = fallbackData.value
                                localStorage.setItem('currentUserContactId', currentUserContactId!)
                            }
                        }
                    } catch { /* ignore */ }
                }

                // Log the request payload for debugging
                const requestPayload = {
                    subject: this.subject,
                    message: this.message,
                    companyId: this.companyId,
                    companyInternalId: companyInternalId, // Include the selected company's internal ID
                    status: 'open',
                    createdBy: authStore.person?.personId,
                    userEmail: authStore.person?.email,
                    userName: `${authStore.person?.firstName || ''} ${authStore.person?.lastName || ''}`.trim(),
                    ticket_type_id: await this.getTicketTypeId(), // Use appropriate ticket type ID based on user role
                    currentUserContactId: currentUserContactId, // Include current user's contact ID to exclude from auto-reply
                }


                // Create new ticket using the proxy endpoint
                const ticketResponse = await fetch('/proxy/tickets', {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Token: token,
                    },
                    body: JSON.stringify(requestPayload),
                })

                // Read the response body once
                const responseText = await ticketResponse.text()
                let responseData: any = {}

                try {
                    responseData = JSON.parse(responseText)
                } catch (e) {
                    console.error('Error parsing response:', e)
                    responseData = {}
                }

                if (!ticketResponse.ok) {
                    console.error('Error response from ticket creation:', {
                        status: ticketResponse.status,
                        statusText: ticketResponse.statusText,
                        responseData,
                        responseText,
                    })
                    const err = new Error(
                        responseData.message || `Error: ${ticketResponse.status} - ${ticketResponse.statusText}`
                    )
                    ;(err as any).debug = responseData.debug
                    throw err
                }

                // Use the already parsed response data
                const ticketData = responseData


                // Reset form
                this.subject = ''
                this.message = ''

                // Emit success event
                this.$emit('ticket-created', ticketData)

                // Force close the dialog using the dedicated method
                this.$emit('update:modelValue', false)
            } catch (error: any) {
                console.error('Error creating ticket:', {
                    message: error.message,
                    stack: error.stack,
                    name: error.name,
                    code: error.code,
                    cause: error.cause,
                })

                // Show error message to user
                this.$emit('error', error.message || 'Failed to create ticket. Please try again.')

                // Log debug info to console (copyable)
                if ((error as any).debug) {
                    console.error('Ticket creation debug info:', JSON.stringify((error as any).debug, null, 2))
                }
                alert(`Error creating ticket: ${error.message}`)
            } finally {
                this.isSubmitting = false
            }
        }

        // Debug auth information
        async debugAuth() {
            try {
                const token = useCookie('token').value

                if (!token) {
                    console.error('No token found')
                    return
                }

                const response = await fetch('/proxy/debug-auth', {
                    headers: {
                        Accept: 'application/json',
                        Token: token,
                    },
                })

                const data = await response.json()

                // Also log local auth information

                alert('Auth debug info logged to console')
            } catch (error) {
                console.error('Error debugging auth:', error)
                alert('Error debugging auth. See console for details.')
            }
        }

        // Get the appropriate ticket type ID based on user role
        async getTicketTypeId(): Promise<string> {
            // Log user role information for debugging
            const role = authStore.person?.role || ''

            const token = useCookie('token').value

            // If the user has TO1 role and we're on the Hotellier Messages tab, use the Questions ticket type ID
            if (this.isTO1User) {
                let questionsTicketTypeId = localStorage.getItem('questionsTicketTypeId')
                // MongoDB fallback
                if (!questionsTicketTypeId && token) {
                    try {
                        const resp = await fetch('/proxy/messaging/config?key=questionsTicketTypeId', {
                            headers: {Accept: 'application/json', Token: token},
                        })
                        if (resp.ok) {
                            const data = await resp.json()
                            if (data.value) {
                                questionsTicketTypeId = data.value
                                localStorage.setItem('questionsTicketTypeId', questionsTicketTypeId!)
                            }
                        }
                    } catch { /* ignore */ }
                }
                if (questionsTicketTypeId) {
                    return questionsTicketTypeId
                }
            }

            // Otherwise, use the default ticket type ID
            let defaultTicketTypeId = localStorage.getItem('defaultTicketTypeId')
            // MongoDB fallback
            if (!defaultTicketTypeId && token) {
                try {
                    const resp = await fetch('/proxy/messaging/config?key=defaultTicketTypeId', {
                        headers: {Accept: 'application/json', Token: token},
                    })
                    if (resp.ok) {
                        const data = await resp.json()
                        if (data.value) {
                            defaultTicketTypeId = data.value
                            localStorage.setItem('defaultTicketTypeId', defaultTicketTypeId!)
                        }
                    }
                } catch { /* ignore */ }
            }
            defaultTicketTypeId = defaultTicketTypeId || '79'
            return defaultTicketTypeId
        }
    }
</script>

<style scoped>
    /* Ensure the modal appears on top of other elements */
    :deep(.v-dialog) {
        z-index: 1000 !important;
    }
</style>
